import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { NgxCurrencyDirective } from 'ngx-currency';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoTarifacaoEnum } from '../../../../shared/enums/financas/tipo-tarifacao.enum';
import { TipoContaEnum } from '../../../../shared/enums/financas/tipo-conta.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FormaRecebimentoService } from '../../../../shared/services/apis/financas/forma-recebimento.service';
import {
  FormaRecebimentoIntermediadorItemRequest,
  FormaRecebimentoRequest,
} from '../../../../shared/services/requests/financas/forma-recebimento.request';
import {
  FormaRecebimentoLookupContaFinanceiraItem,
  FormaRecebimentoLookupItem,
  FormaRecebimentoLookupMeioPagamentoNFeItem,
  FormaRecebimentoLookupOpcaoItem,
  FormaRecebimentoLookupTipoDestinoItem,
} from '../../../../shared/services/structs/financas/forma-recebimento-lookup.struct';
import { FormaRecebimentoIntermediadorItem } from '../../../../shared/services/structs/financas/forma-recebimento-intermediador.struct';
import { DestinosBoletoLookupStruct } from '../../../../shared/services/responses/financas/destinos-boleto-lookup.response';
import { MaskService } from '../../../../shared/services/mask.service';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';

interface DestinoBoletoItem extends DestinosBoletoLookupStruct {
  isHabilitado: boolean;
  isPreferencial: boolean;
}

@Component({
  selector: 'app-forma-recebimento-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatTableModule,
    MatDialogModule,
    NgxCurrencyDirective,
    HeaderComponent,
    LoadingBoxComponent,
    FooterActionComponent,
  ],
  templateUrl: './forma-recebimento-cadastro.component.html',
  styleUrl: './forma-recebimento-cadastro.component.css',
})
export class FormaRecebimentoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;
  public form!: FormGroup;
  public isLoading = false;
  public isSaving = false;
  public isEdicao = false;
  public idFormaRecebimento = 0;
  public isDescricaoBloqueada = false;
  public isDinheiro = false;
  public formasRecebimentoLookup: FormaRecebimentoLookupItem[] = [];
  public contasFinanceirasLookup: FormaRecebimentoLookupContaFinanceiraItem[] = [];
  public tiposDestinoDisponiveis: FormaRecebimentoLookupTipoDestinoItem[] = [];
  public tiposTarifacaoDisponiveis: FormaRecebimentoLookupOpcaoItem[] = [];
  public formasCalculoTaxasDisponiveis: FormaRecebimentoLookupOpcaoItem[] = [];
  public categoriasTarifaDisponiveis: FormaRecebimentoLookupOpcaoItem[] = [];
  public meiosPagamentoNFeDisponiveis: FormaRecebimentoLookupMeioPagamentoNFeItem[] = [];

  public intermediadores: FormaRecebimentoIntermediadorItem[] = [];
  public destinosBoleto: DestinoBoletoItem[] = [];
  public isLoadingDestinosBoleto = false;

  public get contasFinanceirasDisponiveis(): FormaRecebimentoLookupContaFinanceiraItem[] {
    if (this.isDinheiro || this.isCrediarioSelecionado())
      return this.contasFinanceirasLookup.filter(c => c.idTipoConta === TipoContaEnum.ContaCaixa);
    return this.contasFinanceirasLookup;
  }

  private isFavoritoAtual = false;
  private _detailLoaded = false;
  public originalContasVinculadasBoleto: any[] = [];
  public originalIntermediadoresVinculadosBoleto: any[] = [];
  public moneyMask: any;

  constructor(
    private formBuilder: FormBuilder,
    private formaRecebimentoService: FormaRecebimentoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private maskService: MaskService,
    private dialog: MatDialog
  ) {
    this.moneyMask = this.maskService.getCurrencyMask();
  }

  public getDecimalMask(precision: number = 2) {
    return this.maskService.getDecimalMask(precision);
  }

  ngOnInit(): void {
    this.createForm();
    this.form
      .get('idTipoDestino')
      ?.valueChanges.subscribe(() => this.atualizarValidacaoContaFinanceiraDestino());
    this.form.get('idTipoTarifacao')?.valueChanges.subscribe(() => this.atualizarValidacoesTarifacao());
    this.form.get('isAntecipacao')?.valueChanges.subscribe(() => this.atualizarValidacoesAntecipacao());
    this.carregarLookups();

    this.route.params.subscribe(params => {
      const idFormaRecebimentoParam = params['idFormaRecebimento'];
      const idFormaRecebimento = Number(idFormaRecebimentoParam);

      this.idFormaRecebimento = idFormaRecebimento;
      this.isEdicao =
        !!idFormaRecebimentoParam && !Number.isNaN(idFormaRecebimento) && idFormaRecebimento > 0;

      this.atualizarDestinosDisponiveis();

      if (this.isEdicao) {
        this.carregarFormaRecebimento();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      descricao: ['', Validators.required],
      isAtivo: [true],
      isValidarLimiteCredito: [true],
      idMeiosPagamentoNFe: [null as number | null],
      idTipoDestino: [null as number | null],
      idContaFinanceira: [null as number | null],
      idTipoTarifacao: [null as number | null],
      idCategoriaFinanceira: [null as number | null],
      diaTarifa: [null as number | null],
      tarifaFixa: [null as number | null],
      taxaTransacao: [null as number | null],
      taxaAdicionalParcela: [null as number | null],
      isAntecipacao: [false],
      idFormaCalculoTaxas: [null as number | null],
      diasRecebimento: [null as number | null],
      hasJurosMulta: [false],
      percentualJurosDiario: [null as number | null],
      percentualMultaMensal: [null as number | null],
      diasCarenciaMulta: [null as number | null],
    });

    this.form.get('hasJurosMulta')?.valueChanges.subscribe(() => this.atualizarValidacoesJurosMulta());
  }

  private carregarLookups(): void {
    this.formaRecebimentoService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao carregar lookups da forma de recebimento.',
            AlertType.warning
          );
          return;
        }

        this.formasRecebimentoLookup = res.listaFormaRecebimento || [];
        this.contasFinanceirasLookup = (res.listaContaFinanceira || []).filter(
          conta => this.getContaFinanceiraLookupId(conta) !== null
        );
        this.tiposTarifacaoDisponiveis = res.listaTipoTarifacao || [];
        this.formasCalculoTaxasDisponiveis = res.listaFormaCalculoTaxas || [];
        this.categoriasTarifaDisponiveis = res.listaCategoriaFinanceira || [];
        this.meiosPagamentoNFeDisponiveis = res.listaMeiosPagamentoNFe || [];

        if (!this.isEdicao && !this.form.get('idTipoTarifacao')?.value) {
          this.form.get('idTipoTarifacao')?.setValue(TipoTarifacaoEnum.SemTaxas, { emitEvent: false });
        }

        this.atualizarDestinosDisponiveis();
        this.atualizarValidacaoMeioPagamentoNFe();
        this.atualizarValidacaoDestinoValores();
        this.atualizarValidacoesConfiguracoesAdicionais();
        this.atualizarValidacoesTarifacao();
        this.atualizarValidacoesAntecipacao();

        if (this._detailLoaded) {
          this.atualizarValidacaoContaFinanceiraDestino();
        }

        this.verificarChamadaDestinosBoleto();
      },
      error: () => {
        this.alertService.show('Erro', 'Erro ao carregar lookups da forma de recebimento.', AlertType.error);
      },
    });
  }

  private carregarFormaRecebimento(): void {
    this.isLoading = true;
    this.formaRecebimentoService.getById(this.idFormaRecebimento).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao carregar forma de recebimento.',
            AlertType.warning
          );
          this.voltar();
          return;
        }

        const forma = res.formaRecebimento;
        if (!forma) {
          this.alertService.show('Atenção', 'Forma de recebimento não encontrada.', AlertType.warning);
          this.voltar();
          return;
        }

        this.isFavoritoAtual = !!forma.isFavorito;
        this.isDescricaoBloqueada = !!forma.isPadrao;
        this.isDinheiro = String(forma.codigoPagamentoSefaz) === '1' || String(forma.codigoPagamentoSefaz) === '01';

        this.form.patchValue({
          descricao: forma.descricao || '',
          isAtivo: !!forma.isAtivo,
          isValidarLimiteCredito: this.obterValorValidarLimiteCredito(forma),
          idMeiosPagamentoNFe: this.extrairIdMeioPagamentoNFe(forma),
          idTipoDestino: this.extrairIdTipoDestino(forma),
          idContaFinanceira: this.extrairInteiroPositivo(forma.idContaFinanceira),
          idTipoTarifacao: this.extrairInteiroPositivo(forma.idTipoTarifacao),
          idCategoriaFinanceira: this.extrairInteiroPositivo(forma.idCategoriaFinanceira),
          diaTarifa: this.extrairInteiroPositivo(forma.diaTarifa),
          tarifaFixa: this.extrairNumero(forma.tarifaFixa),
          taxaTransacao: this.extrairNumero(forma.taxaTransacao),
          taxaAdicionalParcela: this.extrairNumero(forma.taxaAdicionalParcela),
          isAntecipacao: !!forma.isAntecipacao,
          idFormaCalculoTaxas: this.extrairInteiroPositivo(forma.idFormaCalculoTaxas),
          diasRecebimento: this.extrairInteiroPositivo(forma.diasRecebimento),
          hasJurosMulta: !!forma.hasJurosMulta,
          percentualJurosDiario: this.extrairNumero(forma.percentualJurosDiario),
          percentualMultaMensal: this.extrairNumero(forma.percentualMultaMensal),
          diasCarenciaMulta: this.extrairInteiroPositivo(forma.diasCarenciaMulta),
        });

        if (this.isDescricaoBloqueada) {
          this.form.get('descricao')?.disable({ emitEvent: false });
        }

        this.intermediadores = forma.intermediadores || [];
        this._detailLoaded = true;
        this.atualizarDestinosDisponiveis();
        this.atualizarValidacaoMeioPagamentoNFe();
        this.atualizarValidacaoDestinoValores();
        this.atualizarValidacaoContaFinanceiraDestino();

        if (this.formasRecebimentoLookup.length > 0) {
          this.atualizarValidacoesConfiguracoesAdicionais();
          this.atualizarValidacoesTarifacao();
          this.atualizarValidacoesAntecipacao();
          this.atualizarValidacoesJurosMulta();
        }

        this.originalContasVinculadasBoleto = forma.contasVinculadas || [];
        this.originalIntermediadoresVinculadosBoleto = forma.intermediadoresVinculados || [];

        this.verificarChamadaDestinosBoleto();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar forma de recebimento.', AlertType.error);
        this.voltar();
      },
    });
  }

  // --- data extraction helpers ---

  private obterValorValidarLimiteCredito(forma: any): boolean {
    const valor = forma?.isValidarLimiteCredito ?? forma?.validarLimiteCredito;
    return valor !== false;
  }

  private extrairIdTipoDestino(forma: any): number | null {
    const idDireto = this.extrairInteiroPositivo(forma?.idTipoDestino);
    if (idDireto !== null) return idDireto;

    const lista = forma?.listaTipoDestino;
    if (!Array.isArray(lista)) return null;
    return lista.map((item: any) => this.extrairInteiroPositivo(item?.id)).find(id => id !== null) ?? null;
  }

  private extrairIdMeioPagamentoNFe(forma: any): number | null {
    const idDireto = this.extrairInteiroPositivo(forma?.idMeiosPagamentoNFe);
    if (idDireto !== null) return idDireto;

    const codigoSefaz = Number(forma?.codigoPagamentoSefaz);
    if (Number.isNaN(codigoSefaz)) return null;
    return this.meiosPagamentoNFeDisponiveis.find(item => item.codigoSefaz === codigoSefaz)?.id ?? null;
  }

  public getContaFinanceiraLookupId(conta: FormaRecebimentoLookupContaFinanceiraItem): number | null {
    return this.extrairInteiroPositivo(conta?.id) ?? this.extrairInteiroPositivo(conta?.idContaFinanceira);
  }

  private extrairInteiroPositivo(value: unknown): number | null {
    const n = Number(value);
    return !Number.isNaN(n) && Number.isInteger(n) && n > 0 ? n : null;
  }

  private extrairNumero(value: unknown): number | null {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }

  // --- destinos ---

  private atualizarDestinosDisponiveis(): void {
    const control = this.form.get('idTipoDestino');

    if (!this.exibeDestinoValores()) {
      this.tiposDestinoDisponiveis = [];
      control?.setValue(null, { emitEvent: false });
      this.form.get('idContaFinanceira')?.setValue(null, { emitEvent: false });
      this.atualizarValidacaoDestinoValores();
      this.atualizarValidacaoContaFinanceiraDestino();
      return;
    }

    if (!this.formasRecebimentoLookup.length) {
      this.tiposDestinoDisponiveis = [];
      return;
    }

    const formaLookup = this.formaLookupAtual;
    const formaOutros = this.formasRecebimentoLookup.find(
      item => this.normalizeText(item.descricao) === 'outros'
    );

    const destinos = formaLookup?.listaTipoDestino?.length
      ? formaLookup.listaTipoDestino
      : formaOutros?.listaTipoDestino?.length
        ? formaOutros.listaTipoDestino
        : this.extrairDestinosUnicos(this.formasRecebimentoLookup);

    this.tiposDestinoDisponiveis = destinos;

    if (!control) return;

    const valorAtual = Number(control.value);
    const possuiValorAtual = !Number.isNaN(valorAtual) && valorAtual > 0;

    if (!possuiValorAtual) {
      const destinosDefault = formaLookup?.listaTipoDestino?.length
        ? formaLookup.listaTipoDestino
        : formaOutros?.listaTipoDestino || [];

      if (destinosDefault.length) {
        const idDestinoDefault = !this.isEdicao
          ? (this.obterIdDestinoContasAReceber(destinosDefault) ?? destinosDefault[0].id)
          : destinosDefault[0].id;
        control.setValue(idDestinoDefault, { emitEvent: false });
      }
      return;
    }

    if (!destinos.some(d => d.id === valorAtual)) {
      control.setValue(destinos[0]?.id ?? null, { emitEvent: false });
    }
    this.atualizarValidacaoContaFinanceiraDestino();
  }

  private extrairDestinosUnicos(
    lookup: FormaRecebimentoLookupItem[]
  ): FormaRecebimentoLookupTipoDestinoItem[] {
    const mapa = new Map<number, FormaRecebimentoLookupTipoDestinoItem>();
    lookup.forEach(forma => {
      (forma.listaTipoDestino || []).forEach(destino => {
        if (!mapa.has(destino.id)) mapa.set(destino.id, destino);
      });
    });
    return Array.from(mapa.values()).sort((a, b) => a.descricao.localeCompare(b.descricao));
  }

  private obterIdDestinoContasAReceber(destinos: FormaRecebimentoLookupTipoDestinoItem[]): number | null {
    return destinos.find(d => this.normalizeText(d.descricao) === 'contas a receber')?.id ?? null;
  }

  private normalizeText(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  // --- visibility predicates ---

  private get formaLookupAtual(): FormaRecebimentoLookupItem | undefined {
    return this.formasRecebimentoLookup.find(item => item.idFormaRecebimento === this.idFormaRecebimento);
  }

  public isDestinoContaFinanceiraSelecionado(): boolean {
    const id = Number(this.form?.get('idTipoDestino')?.value);
    if (Number.isNaN(id) || id <= 0) return false;
    return this.normalizeText(this.tiposDestinoDisponiveis.find(d => d.id === id)?.descricao).includes(
      'conta financeira'
    );
  }

  public isTarifacaoSemTaxasSelecionada(): boolean {
    return Number(this.form?.get('idTipoTarifacao')?.value) === TipoTarifacaoEnum.SemTaxas;
  }

  public isTarifacaoEmDiaEspecificoSelecionada(): boolean {
    return Number(this.form?.get('idTipoTarifacao')?.value) === TipoTarifacaoEnum.EmDiaEspecifico;
  }

  public exibeCamposTarifaDetalhados(): boolean {
    const id = Number(this.form?.get('idTipoTarifacao')?.value);
    return id > 0 && id !== TipoTarifacaoEnum.SemTaxas;
  }

  public exibeCampoDiaTarifa(): boolean {
    return this.exibeCamposTarifaDetalhados() && this.isTarifacaoEmDiaEspecificoSelecionada();
  }

  public exibeCamposAntecipacao(): boolean {
    return !!this.form?.get('isAntecipacao')?.value;
  }

  public exibeConfiguracoesAdicionais(): boolean {
    if (!this.isEdicao || !this.isDescricaoBloqueada) return true;
    return this.normalizeText(this.formaLookupAtual?.descricao) === 'outros';
  }

  public exibeMeiosPagamentoNFe(): boolean {
    if (!this.isEdicao || !this.isDescricaoBloqueada) return true;
    const descricao =
      this.normalizeText(this.formaLookupAtual?.descricao) ||
      this.normalizeText(this.form?.get('descricao')?.value);
    return descricao === 'outros' || descricao === 'vale-troca' || descricao === 'vale troca';
  }

  public exibeDestinoValores(): boolean {
    return (
      !this.isBoletoForm() &&
      !this.isCartaoForm() &&
      !this.isPixForm() &&
      !this.isValeTrocaForm()
    );
  }

  public isCrediarioSelecionado(): boolean {
    const descricao =
      this.normalizeText(this.formaLookupAtual?.descricao) ||
      this.normalizeText(this.form?.get('descricao')?.value);
    return descricao === 'crediario';
  }

  public isValeTrocaForm(): boolean {
    const descricao =
      this.normalizeText(this.formaLookupAtual?.descricao) ||
      this.normalizeText(this.form?.get('descricao')?.value);
    return this.isDescricaoBloqueada && (descricao === 'vale-troca' || descricao === 'vale troca');
  }

  private getMeioPagamentoNFeSelecionado(): FormaRecebimentoLookupMeioPagamentoNFeItem | null {
    const id = this.extrairInteiroPositivo(this.form?.get('idMeiosPagamentoNFe')?.value);
    return id !== null ? (this.meiosPagamentoNFeDisponiveis.find(item => item.id === id) ?? null) : null;
  }

  // --- validation helpers ---

  private setControlRequired(control: AbstractControl | null, required: boolean): void {
    if (!control) return;
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.setValue(null, { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private atualizarValidacaoMeioPagamentoNFe(): void {
    this.setControlRequired(this.form.get('idMeiosPagamentoNFe'), this.exibeMeiosPagamentoNFe());
  }

  private atualizarValidacaoDestinoValores(): void {
    this.setControlRequired(this.form.get('idTipoDestino'), this.exibeDestinoValores());
  }

  private atualizarValidacaoContaFinanceiraDestino(): void {
    this.setControlRequired(
      this.form.get('idContaFinanceira'),
      this.exibeDestinoValores() && this.isDestinoContaFinanceiraSelecionado()
    );
  }

  private atualizarValidacoesConfiguracoesAdicionais(): void {
    const exibe = this.exibeConfiguracoesAdicionais();
    this.setControlRequired(this.form.get('idTipoTarifacao'), exibe);
    if (!exibe) {
      [
        'idCategoriaFinanceira',
        'diaTarifa',
        'tarifaFixa',
        'taxaTransacao',
        'taxaAdicionalParcela',
        'idFormaCalculoTaxas',
        'diasRecebimento',
      ].forEach(field => this.form.get(field)?.setValue(null, { emitEvent: false }));
      this.form.get('isAntecipacao')?.setValue(false, { emitEvent: false });
    }
  }

  private atualizarValidacoesTarifacao(): void {
    const exibeDetalhes = this.exibeConfiguracoesAdicionais() && this.exibeCamposTarifaDetalhados();
    const exibeDia = exibeDetalhes && this.isTarifacaoEmDiaEspecificoSelecionada();

    this.setControlRequired(this.form.get('idCategoriaFinanceira'), exibeDetalhes);
    this.setControlRequired(this.form.get('tarifaFixa'), exibeDetalhes);
    this.setControlRequired(this.form.get('taxaTransacao'), exibeDetalhes);
    this.setControlRequired(this.form.get('taxaAdicionalParcela'), exibeDetalhes);

    const diaTarifaControl = this.form.get('diaTarifa');
    if (diaTarifaControl) {
      if (exibeDia) {
        diaTarifaControl.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(31),
          Validators.pattern(/^\d+$/),
        ]);
      } else {
        diaTarifaControl.clearValidators();
        diaTarifaControl.setValue(null, { emitEvent: false });
      }
      diaTarifaControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private atualizarValidacoesAntecipacao(): void {
    const exibe = this.exibeConfiguracoesAdicionais() && this.exibeCamposAntecipacao();
    this.setControlRequired(this.form.get('idFormaCalculoTaxas'), exibe);

    const diasControl = this.form.get('diasRecebimento');
    if (diasControl) {
      if (exibe) {
        diasControl.setValidators([Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]);
      } else {
        diasControl.clearValidators();
        diasControl.setValue(null, { emitEvent: false });
      }
      diasControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // --- intermediadores ---

  public isCartaoForm(): boolean {
    const codigo = this.formaLookupAtual?.codigoPagamentoSefaz;
    return this.isDescricaoBloqueada && (codigo === '03' || codigo === '04');
  }

  public exibeIntermediadoresDiretos(): boolean {
    const codigo = this.formaLookupAtual?.codigoPagamentoSefaz;
    return this.isDescricaoBloqueada && (codigo === '03' || codigo === '04');
  }

  public onHabilitadoChange(item: FormaRecebimentoIntermediadorItem, checked: boolean): void {
    item.isHabilitado = checked;
    if (!checked && item.isPreferencial) {
      item.isPreferencial = false;
    }
  }

  public setPreferencial(item: FormaRecebimentoIntermediadorItem): void {
    this.intermediadores.forEach(i => (i.isPreferencial = false));
    item.isPreferencial = true;
    item.isHabilitado = true;
  }

  public isBoletoForm(): boolean {
    const codigo = String(this.formaLookupAtual?.codigoPagamentoSefaz);
    return this.isDescricaoBloqueada && codigo === '15';
  }

  public isPixForm(): boolean {
    const codigo = this.formaLookupAtual?.codigoPagamentoSefaz;
    return this.isDescricaoBloqueada && codigo === '17';
  }

  public isDepositoForm(): boolean {
    const descricao =
      this.normalizeText(this.formaLookupAtual?.descricao) ||
      this.normalizeText(this.form?.get('descricao')?.value);
    return this.isDescricaoBloqueada && descricao === 'deposito';
  }

  public get contasBancariasDeposito(): DestinoBoletoItem[] {
    return this.destinosBoleto.filter(item => !!item.idContaFinanceira);
  }

  private verificarChamadaDestinosBoleto(): void {
    if (this._detailLoaded && this.formasRecebimentoLookup.length > 0) {
      if ((this.isBoletoForm() || this.isDepositoForm() || this.isPixForm()) && this.destinosBoleto.length === 0 && !this.isLoadingDestinosBoleto) {
        this.carregarDestinosBoleto(
          this.originalContasVinculadasBoleto,
          this.originalIntermediadoresVinculadosBoleto
        );
      }
    }
  }

  private carregarDestinosBoleto(contasVinculadas: any[] = [], intermediadoresVinculados: any[] = []): void {
    this.isLoadingDestinosBoleto = true;
    this.formaRecebimentoService.getDestinosBoletoLookup().subscribe({
      next: res => {
        this.isLoadingDestinosBoleto = false;
        if (!res.isError && res.list) {
          this.destinosBoleto = res.list.map(s => {
            let vinculado = null;
            if (s.idContaFinanceira) {
              vinculado = contasVinculadas?.find(c => c.idContaFinanceira === s.idContaFinanceira);
            } else if (s.idIntermediadorPagamento) {
              vinculado = intermediadoresVinculados?.find(
                i => i.idIntermediadorPagamento === s.idIntermediadorPagamento
              );
            }
            return {
              ...s,
              isHabilitado: !!vinculado,
              isPreferencial: !!vinculado?.isPreferencial,
            };
          });
        }
      },
      error: () => (this.isLoadingDestinosBoleto = false),
    });
  }

  public onHabilitadoBoletoChange(item: DestinoBoletoItem, checked: boolean): void {
    item.isHabilitado = checked;
    if (!checked && item.isPreferencial) {
      item.isPreferencial = false;
    }
  }

  public setPreferencialBoleto(item: DestinoBoletoItem): void {
    this.destinosBoleto.forEach(i => (i.isPreferencial = false));
    item.isPreferencial = true;
    item.isHabilitado = true;
  }

  public getDestinoBoletoEditLink(item: DestinoBoletoItem): any[] {
    if (item.idContaFinanceira) {
      return ['/configuracoes/financas/contas-financeiras/cadastrar', item.idContaFinanceira];
    } else {
      return ['/configuracoes/financas/intermediador-pagamento/cadastrar', item.idIntermediadorPagamento];
    }
  }

  private atualizarValidacoesJurosMulta(): void {
    const hasJurosMulta = !!this.form.get('hasJurosMulta')?.value;
    const jurosControl = this.form.get('percentualJurosDiario');
    const multaControl = this.form.get('percentualMultaMensal');
    const carenciaControl = this.form.get('diasCarenciaMulta');

    if (hasJurosMulta) {
      jurosControl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      multaControl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      carenciaControl?.setValidators([Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]);
    } else {
      jurosControl?.clearValidators();
      multaControl?.clearValidators();
      carenciaControl?.clearValidators();

      jurosControl?.setValue(null, { emitEvent: false });
      multaControl?.setValue(null, { emitEvent: false });
      carenciaControl?.setValue(null, { emitEvent: false });
    }

    jurosControl?.updateValueAndValidity({ emitEvent: false });
    multaControl?.updateValueAndValidity({ emitEvent: false });
    carenciaControl?.updateValueAndValidity({ emitEvent: false });
  }

  // --- save helpers ---

  private focarPrimeiroCampoInvalido(): void {
    setTimeout(() => {
      const campo = document.querySelector<HTMLElement>(
        'form .mat-mdc-form-field .ng-invalid[formControlName]'
      );
      if (!campo) return;
      campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo.focus({ preventScroll: true });
    });
  }

  private parseNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(String(value).replace(',', '.').trim());
    return Number.isNaN(n) ? null : n;
  }

  private parseNullablePositiveInt(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isNaN(n) || !Number.isInteger(n) || n <= 0 ? null : n;
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focarPrimeiroCampoInvalido();
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const v = this.form.getRawValue();
    const exibeConfig = this.exibeConfiguracoesAdicionais();
    const exibeDetalhes = exibeConfig && this.exibeCamposTarifaDetalhados();
    const exibeDia = exibeDetalhes && this.isTarifacaoEmDiaEspecificoSelecionada();
    const exibeAntecipacao = exibeConfig && this.exibeCamposAntecipacao();
    const meioPagamento = this.getMeioPagamentoNFeSelecionado();

    const request: FormaRecebimentoRequest = {
      descricao: (v.descricao || '').trim(),
      isAtivo: !!v.isAtivo,
      isFavorito: this.isFavoritoAtual,
      validarLimiteCredito: !!v.isValidarLimiteCredito,
      idMeiosPagamentoNFe: this.parseNullablePositiveInt(v.idMeiosPagamentoNFe),
      codigoPagamentoSefaz: meioPagamento ? String(meioPagamento.codigoSefaz).padStart(2, '0') : null,
      idTipoDestino: this.exibeDestinoValores() && v.idTipoDestino ? Number(v.idTipoDestino) : null,
      idContaFinanceira: this.exibeDestinoValores() && this.isDestinoContaFinanceiraSelecionado()
        ? v.idContaFinanceira
          ? Number(v.idContaFinanceira)
          : null
        : null,
      idTipoTarifacao: exibeConfig && v.idTipoTarifacao ? Number(v.idTipoTarifacao) : null,
      idCategoriaFinanceira: exibeDetalhes ? this.parseNullablePositiveInt(v.idCategoriaFinanceira) : null,
      diaTarifa: exibeDia ? this.parseNullablePositiveInt(v.diaTarifa) : null,
      tarifaFixa: exibeDetalhes ? this.parseNullableNumber(v.tarifaFixa) : null,
      taxaTransacao: exibeDetalhes ? this.parseNullableNumber(v.taxaTransacao) : null,
      taxaAdicionalParcela: exibeDetalhes ? this.parseNullableNumber(v.taxaAdicionalParcela) : null,
      isAntecipacao: exibeConfig && !!v.isAntecipacao,
      idFormaCalculoTaxas: exibeAntecipacao ? this.parseNullablePositiveInt(v.idFormaCalculoTaxas) : null,
      diasRecebimento: exibeAntecipacao ? this.parseNullablePositiveInt(v.diasRecebimento) : null,

      hasJurosMulta: this.isCrediarioSelecionado() ? !!v.hasJurosMulta : false,
      percentualJurosDiario:
        this.isCrediarioSelecionado() && v.hasJurosMulta
          ? this.parseNullableNumber(v.percentualJurosDiario)
          : null,
      percentualMultaMensal:
        this.isCrediarioSelecionado() && v.hasJurosMulta
          ? this.parseNullableNumber(v.percentualMultaMensal)
          : null,
      diasCarenciaMulta:
        this.isCrediarioSelecionado() && v.hasJurosMulta
          ? this.parseNullablePositiveInt(v.diasCarenciaMulta)
          : null,
      contasVinculadas:
        this.isEdicao && (this.isBoletoForm() || this.isDepositoForm() || this.isPixForm())
          ? this.destinosBoleto
            .filter(d => d.idContaFinanceira && d.isHabilitado)
            .map(d => ({ idContaFinanceira: d.idContaFinanceira!, isPreferencial: d.isPreferencial }))
          : null,
      intermediadores:
        this.isEdicao && this.exibeIntermediadoresDiretos()
          ? this.intermediadores.map(
            ({
              idIntermediadorPagamento,
              isHabilitado,
              isPreferencial,
            }): FormaRecebimentoIntermediadorItemRequest => ({
              idIntermediadorPagamento,
              isHabilitado,
              isPreferencial,
            })
          )
          : this.isEdicao && (this.isBoletoForm() || this.isPixForm())
            ? this.destinosBoleto
              .filter(d => d.idIntermediadorPagamento && d.isHabilitado)
              .map(d => ({
                idIntermediadorPagamento: d.idIntermediadorPagamento!,
                isHabilitado: true,
                isPreferencial: d.isPreferencial,
              }))
            : null,
    };

    const salvar$ = this.isEdicao
      ? this.formaRecebimentoService.put(this.idFormaRecebimento, request)
      : this.formaRecebimentoService.post(request);

    salvar$.subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show(
            'Atenção',
            res.errorDescription || 'Erro ao salvar forma de recebimento.',
            AlertType.warning
          );
          return;
        }

        this.alertService.show(
          'Sucesso',
          `Forma de recebimento ${this.isEdicao ? 'atualizada' : 'cadastrada'} com sucesso.`,
          AlertType.success
        );
        this.voltar();
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Erro ao salvar forma de recebimento.', AlertType.error);
      },
    });
  }

  public refreshDestinosBoleto(): void {
    this.carregarDestinosBoleto(
      this.originalContasVinculadasBoleto,
      this.originalIntermediadoresVinculadosBoleto
    );
  }

  public confirmarRedirecionamento(destinoNome: string, commands: any[], queryParams?: any): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '450px',
      data: {
        title: 'Atenção: Dados Não Salvos',
        buttonConfirm: 'Continuar',
        buttonClose: 'Cancelar',
        innerHtml: `Você será redirecionado para <strong>${destinoNome}</strong> em uma nova aba.<br><br><strong>Atenção:</strong> As alterações desta forma de recebimento <strong>ainda não foram salvas</strong>.<br><br>Deseja continuar?`,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.confirm) {
        const urlTree = this.router.createUrlTree(commands, { queryParams: queryParams || null });
        const url = this.router.serializeUrl(urlTree);
        window.open(url, '_blank');
      }
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/formas-recebimento']);
  }
}
